import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import SVG from 'react-inlinesvg'
import AniLink from 'gatsby-plugin-transition-link/AniLink'
import moment from 'moment'

import IconChevron from '../../assets/svg/arrow.svg'

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  margin: ${({ small }) => (small ? `20px -30px 0 -70px` : `40px 0 0 -90px`)};
  list-style: none;

  :after {
    z-index: 0;
    content: '';
    position: absolute;
    top: 0;
    left: -150px;
    width: 150px;
    bottom: 0;
    background: -moz-linear-gradient(
      left,
      rgba(19, 19, 19, 1) 0%,
      rgba(16, 16, 16, 1) 10%,
      rgba(0, 0, 0, 0) 100%
    );
    background: -webkit-linear-gradient(
      left,
      rgba(19, 19, 19, 1) 0%,
      rgba(16, 16, 16, 1) 10%,
      rgba(0, 0, 0, 0) 100%
    );
    background: linear-gradient(
      to right,
      rgba(19, 19, 19, 1) 0%,
      rgba(16, 16, 16, 1) 10%,
      rgba(0, 0, 0, 0) 100%
    );
    filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#131313', endColorstr='#00000000',GradientType=1 );
  }

  ${({ theme }) => theme.media.l} {
    margin: ${({ small }) => (small ? `20px 0 0 -30px` : `60px 0 0 -30px`)};
  }

  ${({ theme }) => theme.media.xl} {
    margin: ${({ small }) => (small ? `20px 0 0 -30px` : `100px 0 0 -30px`)};
  }
`

const Container = styled.ul`
  overflow-x: scroll;
  white-space: nowrap;
  margin: 0 auto;
  padding: 0 30px 30px;
  width: 100%;
  max-width: ${({ theme }) => theme.sizes.maxContentWidth};

  ::-webkit-scrollbar {
    width: 12px;
    background-color: rgba(0, 0, 0, 0);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: ${({ theme }) => theme.colors.primary};
  }

  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0);
  }

  ${({ theme }) => theme.media.l} {
    overflow-x: visible;
    padding: 0 30px;
  }
`

const NewsItem = styled.li.attrs(({ currentSlider }) => ({
  style: {
    transform: `translate3d(-${currentSlider * 100}%, 0, 0)`,
  },
}))`
  overflow: hidden;
  position: relative;
  display: inline-block;
  padding: 0 30px 0 0;
  text-align: right;
  transition: all 0.3s ease;

  div {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    ${({ small }) =>
      small
        ? css`
            width: 156px;
            height: 180px;
          `
        : css`
            width: 225px;
            height: 270px;
          `} margin: 0;
    background: center center url(${({ bg }) => bg}) no-repeat;
    background-size: cover;
    font-weight: ${({ theme }) => theme.font.light};
    opacity: 0.6;
    transition: all 0.3s ease;
    cursor: pointer;

    > button {
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    :hover {
      opacity: 1;
      > button {
        opacity: 1;
      }
    }
  }

  h3 {
    margin: 20px 0 10px;
    font-size: 16px;
    font-weight: ${({ theme }) => theme.font.bold};
    color: ${({ theme }) => theme.colors.primary};
  }

  p {
    position: relative;
    display: inline-block;

    :after {
      position: absolute;
      top: 50%;
      right: 100%;
      width: 100vw;
      height: 1px;
      margin-right: 20px;
      background: ${({ theme }) => theme.colors.primary};
      opacity: 0.8;
      content: '';
    }
  }

  ${({ theme }) => theme.media.m} {
    h3 {
      margin: 25px 0 15px;
      font-size: 18px;
    }
    div {
      ${({ small }) =>
        small
          ? css`
              width: 208px;
              height: 240px;
            `
          : css`
              width: 256px;
              height: 320px;
            `}
    }
  }

  ${({ theme }) => theme.media.xl} {
    h3 {
      margin: 30px 0 20px;
      font-size: 22px;
    }
    div {
      ${({ small }) =>
        small
          ? css`
              width: 260px;
              height: 300px;
            `
          : css`
              width: 320px;
              height: 400px;
            `}
    }
  }
`

const Navigation = styled.ul`
  z-index: 1;
  position: absolute;
  top: 30%;
  left: -40px;
  display: none;

  li {
    display: flex;
    align-items: center:
    justify-content: center;
    margin: 0 0 25px;
    cursor: pointer;

    svg {
      width: 35px;
      height: 35px;
      
      path {
        fill: ${({ theme }) => theme.colors.primary};
        transition: fill .3s ease;
      }
    }

    :hover svg path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }

  ${({ theme }) => theme.media.l} {
    display: block;
  }
`

const NavPrev = styled.li`
  transform: rotate(180deg);
`

const NavNext = styled.li`
  right: 6px;
`

const EmptyState = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${({ small }) =>
    small
      ? css`
          height: 180px;
        `
      : css`
          height: 270px;
        `}
  text-align: center;

  p {
    margin: 0 0 10px;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const CurrentItem = styled.div`
  z-index: 900;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  transition: opacity 0.6s 0.3s ease;
  cursor: pointer;

  > img {
    height: 80%;
  }

  ${({ showed }) =>
    showed
      ? css`
          opacity: 1;
          pointer-events: auto;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `};
`

const NewsList = ({ newsList, small }) => {
  const [currentSlider, setCurrentSlider] = useState(0)
  const [currentItem, setCurrentItem] = useState(0)
  const [currentItemShowed, setCurrentItemShowed] = useState(false)

  const today = new Date()
  const dd = String(today.getDate()).padStart(2, '0')
  const mm = String(today.getMonth() + 1).padStart(2, '0') //January is 0!
  const yyyy = today.getFullYear()
  const todayDate = `${yyyy}-${mm}-${dd}`

  const promotions = newsList.filter(item =>
    moment(todayDate).isBetween(item.startDate, item.endDate, null, '[]')
  )

  const setPrev = () => {
    currentSlider !== 0 && setCurrentSlider(currentSlider - 1)
  }

  const setNext = () => {
    currentSlider !== newsList.length - 1 && setCurrentSlider(currentSlider + 1)
  }

  const handleClick = index => {
    setCurrentItem(index)
    setCurrentItemShowed(true)
  }

  return (
    <Wrapper small={small}>
      <Container>
        {promotions.length !== 0 ? (
          promotions.map((item, index) => (
            <NewsItem
              key={item.id}
              small={small}
              bg={item.poster?.childImageSharp.fluid?.src}
              currentSlider={currentSlider}
              onClick={() => handleClick(index)}
            >
              <div />
              {!small && <h3>{/*item.name*/}</h3>}
              {!small && item.restaurants.length === 1 && (
                <p>{item.restaurants[0]['Name']}</p>
              )}
              {!small && item.restaurants.length > 1 && (
                <p></p>
              )}
            </NewsItem>
          ))
        ) : (
          <EmptyState>
            <p>Excitement is on the way. Stay tuned…</p>
          </EmptyState>
        )}
      </Container>
      <Navigation>
        <NavNext onClick={() => setNext()}>
          <SVG src={IconChevron} />
        </NavNext>
        <NavPrev onClick={() => setPrev()}>
          <SVG src={IconChevron} />
        </NavPrev>
      </Navigation>
      {promotions.length !== 0 && (
        <CurrentItem
          showed={currentItemShowed}
          onClick={() => setCurrentItemShowed(false)}
        >
          <img
            src={promotions[currentItem].poster?.childImageSharp.fluid?.src}
            alt="News"
          />
        </CurrentItem>
      )}
    </Wrapper>
  )
}

export default NewsList
